/**
 * 외부 브라우저 열기 유틸 — 앱(WebView) / 모바일웹 / 데스크탑웹 분기
 *
 *  멍투데이 앱에서 어떤 URL 을 SPA 라우터로 이동하지 않고
 *  시스템 기본 브라우저(Safari/Chrome 등)로 열고 싶을 때 사용.
 *
 *  네이티브 브리지 우선순위:
 *   1) iOS  : window.webkit.messageHandlers.mung.postMessage(["openExternalUrl", url])
 *   2) Android : window.mung.openExternalUrl(url)
 *   3) 브리지 미구현 환경 : window.open(url, "_blank") 로 fallback
 *      → 안드로이드 WebView 의 경우 보통 OS 가 외부 브라우저로 잡아냄.
 *
 *  ⚠ 네이티브 측 구현 필요:
 *    iOS  — `openExternalUrl` 핸들러 추가: `UIApplication.shared.open(URL)` 호출
 *    Android — `openExternalUrl(String url)` 자바스크립트 인터페이스 추가:
 *      `Intent.ACTION_VIEW` + `setData(Uri.parse(url))` + `startActivity(...)`
 */

/** UA 기준 멍투데이 앱(WebView) 판별. */
export const isMungApp = (): boolean => {
  if (typeof navigator === "undefined") return false;
  return /MT_I|MT_A/i.test(navigator.userAgent);
};

/** UA 기준 iOS 앱 판별. */
export const isMungAppIOS = (): boolean => {
  if (typeof navigator === "undefined") return false;
  return /MT_I/i.test(navigator.userAgent);
};

/** UA 기준 Android 앱 판별. */
export const isMungAppAndroid = (): boolean => {
  if (typeof navigator === "undefined") return false;
  return /MT_A/i.test(navigator.userAgent);
};

/**
 * 외부 브라우저로 URL 열기.
 *  - 멍투데이 앱(iOS/Android) → 네이티브 브리지로 외부 브라우저 열기 요청
 *  - 일반 모바일웹/데스크탑웹 → 새 탭 (window.open)
 *
 *  네이티브 브리지가 등록 안 돼있어도 window.open fallback 으로 동작.
 *
 *  @param url 절대 URL 권장 (http:// or https://). 상대 경로면 origin 자동 prefix.
 */
export const openExternalBrowser = (url: string): void => {
  // 상대 경로 → 절대 URL 변환
  let absoluteUrl = url;
  try {
    absoluteUrl = new URL(url, window.location.origin).toString();
  } catch {
    // 잘못된 URL — 그대로 시도
  }

  const w = window as any;

  // 1) iOS 앱 — webkit 브리지
  if (isMungAppIOS()) {
    const ios = w.webkit?.messageHandlers?.mung;
    if (ios && typeof ios.postMessage === "function") {
      try {
        ios.postMessage(["openExternalUrl", absoluteUrl]);
        return;
      } catch (e) {
        console.warn("[openExternalBrowser] iOS bridge failed, fallback:", e);
      }
    }
  }

  // 2) Android 앱 — window.mung 브리지
  if (isMungAppAndroid()) {
    if (w.mung && typeof w.mung.openExternalUrl === "function") {
      try {
        w.mung.openExternalUrl(absoluteUrl);
        return;
      } catch (e) {
        console.warn("[openExternalBrowser] Android bridge failed, fallback:", e);
      }
    }
  }

  // 3) Fallback — 일반 웹 또는 브리지 미구현
  window.open(absoluteUrl, "_blank", "noopener,noreferrer");
};
